// Shared helpers for deciding whether a deal stage is "gated" (>=50%) and
// requires financial capture (TCI/TCV/OneTime/MRR) before entry.
//
// Backend contract (tasks/stages/): each stage is { id, name, percentage }
// where percentage is int|null. null means the stage (e.g. Qualified
// Meetings) is known to be NOT gated — it must NOT fall through to the
// label-regex fallback. Only an absent/undefined entry (stage id missing
// from the map) is "unknown" and falls back to parsing the stage name/label.

/**
 * Build a { [stageId]: percentage } map from the tasks/stages payload.
 * @param {Array<{id: any, name?: string, percentage: number|null}>} stages
 * @returns {Object<string|number, number|null>}
 */
export function buildStagePercentMap(stages) {
  const map = {};
  (stages || []).forEach((s) => {
    map[s.id] = s.percentage;
  });
  return map;
}

/**
 * A stage is "gated" iff its authoritative percentage >= 50.
 * - percentMap[stage.id] === null -> known, NOT gated (e.g. Qualified Meetings).
 * - percentMap[stage.id] is a number -> gated iff >= 50.
 * - percentMap[stage.id] === undefined (stage absent from map) -> fall back
 *   to parsing "- N%" out of stage.name/stage.label.
 * @param {{id: any, name?: string, label?: string}} stage
 * @param {Object<string|number, number|null>} percentMap
 * @returns {boolean}
 */
export function isGatedStage(stage, percentMap) {
  if (!stage) return false;

  const p = percentMap ? percentMap[stage.id] : undefined;

  if (p === null) return false; // known: e.g. Qualified Meetings, not gated
  if (p !== undefined) return Number(p) >= 50;

  // unknown -> label fallback
  const text = stage.name || stage.label || '';
  const m = text.match(/-\s*(\d+)\s*%/);
  return m ? Number(m[1]) >= 50 : false;
}
